import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Recommend } from 'api';
import type { MeetingExtendSuccessPayload } from 'api/recommend';
import APP_ROUTE from 'constant/route';
import { getStaffId, getUserId } from 'data/storage';
import { OrderSettings } from 'type/meeting';
import { buildExtendMeetingRebookUrl } from 'util/meetingExtendRebook';
import { showExtendMeetingError } from 'util/showExtendMeetingError';

// TODO 组件库生成
interface IDetailRef {
  isAdmin: boolean;
  currentUserId: string;
  meetingInfo: any;
  meetingSummary: string;
  addEventListener: any;
  removeEventListener: any;
  linkList: OrderSettings['linkList'];
  uploadFile: any;
  downloadFile: any;
  previewFile: any;
  detailSettings: OrderSettings;
  platformPlaceholder: string;
  service: {
    extendMeeting: (meetingNo: string, data: Parameters<typeof Recommend.extendMeeting>[1]) => Promise<unknown>;
    onExtendMeetingError: (error: unknown) => void;
  };
  extendMeetingEnabled: boolean;
  extendMeetingMinutes: number;
}

// 该组件作为防腐层，封装 web-component
interface IProps {
  meetingInfo: any;
  uploadFile: any;
  downloadFile?: any;
  previewFile?: any;
  meetingSummary: string;
  linkList?: OrderSettings['linkList'];
  platformPlaceholder: string;
  detailSettings: OrderSettings;
  onClickEdit: () => void;
  onClickCancel: () => void;
  onClickSignIn: () => void;
  onClickConclude: () => void;
  onClickRevoke: () => void;
  onClickJoin: () => void;
  onClickAskOff: () => void;
  onClickRefuse: () => void;
  onClickSubmitNotes: (text: string) => void;
  onClickWithdrawInvite: (userId: string) => void;
  onClickInvite: () => void;
  onClickGotIt: () => void;
  onClickAgendaEdit: (text: string) => void;
  onClickEditThis: () => void;
  onClickEditAll: () => void;
  onClickCancelAll: () => void;
  onClickCancelThis: () => void;
  onClickCancelPart: () => void;
  onClickLink: (url: string) => void;
  onClickExtendSuccess?: (payload: MeetingExtendSuccessPayload) => void;
}

const MeetingDetailsWrapper: React.FC<IProps> = (props) => {
  const detailRef = useRef<IDetailRef>(null);
  const history = useHistory();
  const userId = getUserId();
  const staffId = getStaffId();
  const extendMeetingEnabled = props.meetingInfo?.creatorStaffId === staffId;
  const meetingInfoForDetail = useMemo(() => {
    if (props.meetingInfo?.status === 'DURING' && !extendMeetingEnabled) {
      return {
        ...props.meetingInfo,
        organizerUserId: undefined
      };
    }

    return props.meetingInfo;
  }, [props.meetingInfo, extendMeetingEnabled]);
  const detailService = useMemo(
    () => ({
      extendMeeting(meetingNo: string, data: Parameters<typeof Recommend.extendMeeting>[1]) {
        return Recommend.extendMeeting(meetingNo, data).toPromise();
      },
      onExtendMeetingError(error: unknown) {
        showExtendMeetingError(error, () => {
          history.push(buildExtendMeetingRebookUrl(APP_ROUTE.ORDER_MEETING, props.meetingInfo?.meetingNo));
        });
      }
    }),
    [history, props.meetingInfo?.meetingNo]
  );

  const eventHandlers = {
    'click-edit': props.onClickEdit,
    'click-cancel': props.onClickCancel,
    'click-signin': props.onClickSignIn,
    'click-conclude': props.onClickConclude,
    'click-revoke': props.onClickRevoke,
    'click-join': props.onClickJoin,
    'click-askoff': props.onClickAskOff,
    'click-refuse': props.onClickRefuse,
    'click-submitnotes': props.onClickSubmitNotes,
    'click-withdrawinvite': props.onClickWithdrawInvite,
    'click-invite': props.onClickInvite,
    'click-gotit': props.onClickGotIt,
    'click-agendaedit': props.onClickAgendaEdit,
    'click-edit-this': props.onClickEditThis,
    'click-edit-all': props.onClickEditAll,
    'click-cancel-all': props.onClickCancelAll,
    'click-cancel-this': props.onClickCancelThis,
    'click-cancel-part': props.onClickCancelPart,
    'click-link': props.onClickLink
  };

  useEffect(() => {
    // 只在组件挂载后设置属性
    if (detailRef.current && meetingInfoForDetail) {
      // 直接操作 DOM 属性来传递对象
      detailRef.current.meetingInfo = meetingInfoForDetail;
      detailRef.current.meetingSummary = props.meetingSummary;
      detailRef.current.extendMeetingEnabled = extendMeetingEnabled;
      detailRef.current.extendMeetingMinutes = 30;
    }
  }, [meetingInfoForDetail, props.meetingSummary, extendMeetingEnabled]);

  useEffect(() => {
    if (detailRef.current) {
      detailRef.current.currentUserId = userId;
      detailRef.current.uploadFile = props.uploadFile;
      detailRef.current.downloadFile = props.downloadFile;
      detailRef.current.previewFile = props.previewFile;
      detailRef.current.service = detailService;
      detailRef.current.extendMeetingMinutes = 30;
    }

    if (detailRef.current) {
      // 初始化-监听事件
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        detailRef.current?.addEventListener(event, handler);
      });

      // 卸载时-清除监听
      return () => {
        if (detailRef.current) {
          Object.entries(eventHandlers).forEach(([event, handler]) => {
            detailRef.current?.removeEventListener(event, handler);
          });
        }
      };
    }
  }, []);

  useEffect(() => {
    if (!detailRef.current) return;
    detailRef.current.service = detailService;
    detailRef.current.extendMeetingMinutes = 30;
  }, [detailService]);

  useEffect(() => {
    if (!detailRef.current) return;
    detailRef.current.extendMeetingEnabled = extendMeetingEnabled;
    detailRef.current.extendMeetingMinutes = 30;
  }, [extendMeetingEnabled]);

  useEffect(() => {
    const current = detailRef.current;
    if (!current || !props.onClickExtendSuccess) return;

    const handleExtendSuccess = (event: CustomEvent<[MeetingExtendSuccessPayload]>) => {
      const payload = event.detail?.[0];
      if (!payload?.meetingNo || !Number.isFinite(payload.newEndTime)) return;
      props.onClickExtendSuccess?.(payload);
    };

    current.addEventListener('click-extend-success', handleExtendSuccess);

    return () => {
      current.removeEventListener('click-extend-success', handleExtendSuccess);
    };
  }, [props.onClickExtendSuccess]);

  useEffect(() => {
    if (detailRef.current && props.detailSettings) {
      detailRef.current.detailSettings = props.detailSettings;
    }
  }, [props.detailSettings]);

  useEffect(() => {
    if (detailRef.current && props.linkList) {
      detailRef.current.linkList = props.linkList;
    }
    console.log('props.linkList::', props);
  }, [props.linkList]);

  useEffect(() => {
    if (detailRef.current && props.platformPlaceholder) {
      detailRef.current.platformPlaceholder = props.platformPlaceholder;
    }
  }, [props.platformPlaceholder]);

  return <ce-meeting-detail isAdmin={false} ref={detailRef} />;
};

export default MeetingDetailsWrapper;
